import { isExpiredInSydney } from '@l2u/shared/sydneyDates';

/**
 * Pure view-model: turn the flat submission_files set (live + superseded, each
 * carrying replaced_by_file_id) into per-document groups with version chains.
 *
 * A "document" is a live file (superseded_at === null) with no successor. Its
 * versions are that file plus every file that chains into it via
 * replaced_by_file_id (transitively; a single file may replace several).
 * Legacy superseded rows with no chain attach to the group sharing their
 * option_key (or the sole live group, when there is only one to choose from,
 * or nothing else shares its option). A scoped orphan matching no live group
 * heads its own history-only group rather than borrowing an unrelated one.
 * When nothing is live at all, the newest file heads a history-only group.
 *
 * A version is 'current' while live, 'removed' when superseded with removed_at
 * set (0096: the vendor removed it, so nothing replaced it), and 'replaced'
 * otherwise — including unmarked rows removed before 0096.
 *
 * Dependency-free so it is trivially unit-testable.
 */
export type DocVersionFile = {
  id: string;
  file_name: string;
  size_bytes: number | null;
  expires_at: string | null;
  superseded_at: string | null;
  superseded_status?: string | null;
  replaced_by_file_id: string | null;
  /** Set when the vendor removed this file (0096); never set by a replacement. */
  removed_at?: string | null;
  /** Per-option scoping, not per-document: null on a plain multi-file question. */
  option_key?: string | null;
  created_at: string;
  download_url?: string | null;
};

export type DocumentVersion = DocVersionFile & {
  state: 'current' | 'replaced' | 'removed';
  /** 1-based chronological version number within the group (oldest = 1). */
  version: number;
};

export type DocumentGroup = {
  current: DocVersionFile;
  versions: DocumentVersion[];
};

/**
 * Whether a question's file area renders the read-only DocumentTable (version
 * history) instead of the editable FilesEditor. Only document_upload questions
 * with at least one live document ever use the table, and only once the vendor
 * can no longer manage files directly: the answer is locked (under review,
 * approved, not applicable), or we are in the post-submit view where
 * replacements flow through the comment composer. Pre-submit the vendor keeps
 * the editor so uploads can still be removed or replaced.
 */
export function showDocumentTable(opts: {
  questionType: string;
  hasDocuments: boolean;
  locked: boolean;
  postSubmit: boolean;
}): boolean {
  return (
    opts.questionType === 'document_upload' && opts.hasDocuments && (opts.locked || opts.postSubmit)
  );
}

function chronological(a: DocVersionFile, b: DocVersionFile): number {
  return a.created_at === b.created_at ? 0 : a.created_at < b.created_at ? -1 : 1;
}

/**
 * Historical rows carry no replaced_by_file_id: the vendor file-DELETE was a
 * separate request that did not know a replacement had just been registered.
 * Infer the missing edge so those chains still rebuild.
 *
 * The successor is the file created NEAREST IN ABSOLUTE TIME to the orphan's
 * superseded_at. Absolute time matters because the two flows disagree on
 * direction: the documents-page re-upload registers the replacement BEFORE
 * retiring the old file, while the wizard's remove-then-upload does the
 * reverse. Candidates must post-date the orphan, which also keeps the inferred
 * graph acyclic.
 */
function inferSuccessors(files: DocVersionFile[]): Map<string, string> {
  const inferred = new Map<string, string>();
  for (const orphan of files) {
    // A removed file has no successor (0096); unmarked rows keep the inference.
    if (orphan.superseded_at === null || orphan.replaced_by_file_id || orphan.removed_at) continue;
    const target = Date.parse(orphan.superseded_at);
    const bornAt = Date.parse(orphan.created_at);
    let best: DocVersionFile | undefined;
    let bestDelta = Infinity;
    for (const candidate of files) {
      if (candidate.id === orphan.id) continue;
      if (Date.parse(candidate.created_at) <= bornAt) continue;
      // option_key scopes an option, not a document — only ever a veto, and
      // only when both sides actually carry one.
      if (
        orphan.option_key != null &&
        candidate.option_key != null &&
        orphan.option_key !== candidate.option_key
      ) {
        continue;
      }
      const delta = Math.abs(Date.parse(candidate.created_at) - target);
      if (delta < bestDelta) {
        best = candidate;
        bestDelta = delta;
      }
    }
    if (best) inferred.set(orphan.id, best.id);
  }
  return inferred;
}

export function buildDocumentGroups(files: DocVersionFile[]): DocumentGroup[] {
  if (files.length === 0) return [];

  const byId = new Map(files.map((file) => [file.id, file]));

  const inferred = inferSuccessors(files);

  // Predecessors: for each file id, the files that name it as their successor.
  const predecessors = new Map<string, DocVersionFile[]>();
  for (const file of files) {
    const successorId = file.replaced_by_file_id ?? inferred.get(file.id) ?? null;
    if (successorId && byId.has(successorId)) {
      const list = predecessors.get(successorId) ?? [];
      list.push(file);
      predecessors.set(successorId, list);
    }
  }

  const live = files.filter((file) => file.superseded_at === null);
  const heads =
    live.length > 0 ? [...live].sort(chronological) : [[...files].sort(chronological).at(-1)!];

  const claimed = new Set<string>();
  const collectChain = (head: DocVersionFile): DocVersionFile[] => {
    const acc: DocVersionFile[] = [];
    const walk = (file: DocVersionFile) => {
      if (claimed.has(file.id)) return;
      claimed.add(file.id);
      acc.push(file);
      for (const predecessor of predecessors.get(file.id) ?? []) walk(predecessor);
    };
    walk(head);
    return acc;
  };

  const groups = heads.map((head) => ({ current: head, files: collectChain(head) }));

  // Legacy superseded rows with no chain. A row with no option_key is scoped to
  // nothing in particular, so it always falls back to the first group exactly
  // as before. A scoped row honours the same veto inferSuccessors applies: it
  // only ever joins a group whose head shares its option_key. With just one
  // group there is nothing else it could be confused with, so it still falls
  // back there (matches the single-live-group behaviour the veto was designed
  // to leave alone). With two or more groups and no matching option, dumping it
  // into groups[0] would undo the veto via the back door — that file's own
  // live document, if any, is outside this render's file set — so it heads its
  // own history-only group instead of contaminating an unrelated option.
  const orphans = files.filter((file) => !claimed.has(file.id));
  for (const orphan of orphans) {
    if (orphan.option_key == null) {
      groups[0]?.files.push(orphan);
      continue;
    }
    const home = groups.find((g) => g.current.option_key === orphan.option_key);
    if (home) {
      home.files.push(orphan);
    } else if (groups.length <= 1) {
      groups[0]?.files.push(orphan);
    } else {
      groups.push({ current: orphan, files: [orphan] });
    }
  }

  return groups.map((group) => {
    const ordered = [...group.files].sort(chronological); // oldest → newest
    const versions: DocumentVersion[] = ordered.map((file, index) => ({
      ...file,
      state: file.superseded_at === null ? 'current' : file.removed_at ? 'removed' : 'replaced',
      version: index + 1,
    }));
    versions.reverse(); // display newest-first
    return { current: group.current, versions };
  });
}

/**
 * True when any live (non-superseded) file has an expiry date in the past —
 * the condition that opens the renewal door on an approved document question
 * (vendor may reply with a replacement; mirrors the guard in api me.ts).
 */
export function hasExpiredLiveFile(files: DocVersionFile[], now: Date): boolean {
  return files.some((f) => f.superseded_at === null && isExpiredInSydney(f.expires_at, now));
}
